import React from "react";
import { Link } from "react-router-dom";
import IntroVideo from '../videos/Axis_background.mp4';
import VideoComponent from './VideoComponent.js';
import Axis_Logo from '../images/Axis_image6.png';
import HandToHandImg from '../images/hand_to_hand1.jpg';
import WeaponsImg from '../images/weapons1.jpg';
import FirearmsImg from '../images/firearms1.jpg';
import TCCCImg from '../images/combat_medical1.png';
import FAImg from '../images/combat_medicine2.jpg';
import FitnessImg from '../images/fitnessimg1.jpg';
import PageSEO from "../components/seo/PageSEO";
import { useScrollBehavior } from './useScrollBehavior.js';
import "../styles/tactical.css";

export default function AxisTactical() {
  useScrollBehavior();

  return (
    <main className="tactical">
      <PageSEO
        title="Axis Tactical | Physical • Mental • Ethical"
        description="Axis Tactical — a private community of ethically aligned individuals committed to physical, mental, and ethical development."
        path="/tactical"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Axis Tactical",
          "url": (process.env.REACT_APP_SITE_URL || "") + "/tactical"
        }}
      />

      {/* ── Background Video ── */}
      <div className="tactical__bg" aria-hidden="true">
        <VideoComponent videoSrc={IntroVideo} />
      </div>

      <section className="tactical__content">

        {/* ── HERO / INTRO ── */}
        <div className="module" id="tactical__top">
          <div className="tac-hero">
            <div className='tactical__logo'>
              <img src={Axis_Logo} alt="Axis_logo" className="axis_logo" />
            </div>
            <span className="tac-hero__eyebrow">Axis Tactical</span>
            <h1 className="tac-hero__title">By Invitation Only</h1>
            <p className="tac-hero__body">
              If you are reading this, you have been personally invited to join a community
              of ethically aligned individuals committed to the continuous improvement of
              mind, body, and character.
            </p>
            <p className="tac-hero__body">
              The Axis Tactical framework exists for those who understand that ethical
              living sometimes demands the capacity to act — decisively, effectively, and
              with purpose. Physical training, tactical skill, and philosophical grounding
              are not separate disciplines here. They are a single, unified practice, in
              service of the sacred three: Life, The Planet and the Social Contract.
            </p>
            <p className="tac-hero__body">
              Those who join are not simply joining a training group. They are accepting a
              responsibility — to themselves, to each other, and to the broader Axis mission.
              Membership is earned through demonstrated commitment, capability, and character.
              It is maintained the same way.
            </p>
          </div>
        </div>

        {/* ── THREE TIERS ── */}
        <div className="module" id="tactical__tiers">
          <h2 className="module__heading">The Three Tiers</h2>
          <p className="module__subheading">
            Progression within Axis Tactical is earned — not assigned by tenure.
            Each tier carries its own obligations, not just its own capabilities.
          </p>
          <div className="tiers__grid">

            <div className="tier tier--3">
              <div className="tier__badge">Tier 3</div>
              <h3 className="tier__title">Practitioner</h3>
              <p className="tier__body">
                The entry point into Axis Tactical. Practitioners are committed to personal
                development — building physical capability, studying the Axis philosophy, and
                integrating ethical discipline into daily life. Foundational training and
                monthly community participation mark this tier.
              </p>
              <ul className="tier__list">
                <li>Foundational physical training</li>
                <li>Axis philosophy study</li>
                <li>Community participation</li>
                <li>Personal accountability practice</li>
              </ul>
            </div>

            <div className="tier tier--2">
              <div className="tier__badge">Tier 2</div>
              <h3 className="tier__title">Operator</h3>
              <p className="tier__body">
                Operators have demonstrated the capability to defend themselves and others.
                They are active participants in the Axis mission through regular charitable
                action and community service. An Operator's commitment extends beyond the self —
                reliability and demonstrated character are required.
              </p>
              <ul className="tier__list">
                <li>Defensive capability — self and others</li>
                <li>Monthly and annual charity obligations</li>
                <li>Active Axis mission participation</li>
                <li>Mentorship within the Practitioner tier</li>
              </ul>
            </div>

            <div className="tier tier--1">
              <div className="tier__badge">Tier 1</div>
              <h3 className="tier__title">Special Operator</h3>
              <p className="tier__body">
                The highest tier within Axis Tactical. Special Operators are capable of both
                offensive and defensive action and carry a deeper responsibility to the Axis
                mission. Training extends into psychological operations, social organization,
                and strategic action. Skill alone is not sufficient — trust is required.
              </p>
              <ul className="tier__list">
                <li>Offensive and defensive capability</li>
                <li>Psychological operations training</li>
                <li>Social organization and strategic action</li>
                <li>Direct Axis mission execution</li>
              </ul>
            </div>

          </div>
        </div>

        {/* ── DISCIPLINES ── */}
        <div className="module" id="tactical__disciplines">
          <h2 className="module__heading">Training Disciplines</h2>
          <p className="module__subheading">
            Three core domains of study. Each is trained not as an isolated skill set
            but as an expression of the Axis principles applied under pressure.
          </p>

          <div className="disciplines__list">

            <div className="discipline">
              <div className="discipline__image">
                <img src={HandToHandImg} alt="Hand to hand combatives" />
              </div>
              <div className="discipline__text">
                <span className="discipline__label">Domain 01</span>
                <h3 className="discipline__title">Hand to Hand Combatives</h3>
                <p className="discipline__body">
                  The foundation of all tactical capability. Unarmed combatives training
                  builds physical skill, situational awareness, stress inoculation, and the
                  controlled application of force. Axis Tactical draws from proven systems
                  focused on realistic, high-stress application — not sport performance.
                </p>
              </div>
            </div>

            <div className="discipline discipline--reverse">
              <div className="discipline__image">
                <img src={WeaponsImg} alt="Non-firearm weapons" />
              </div>
              <div className="discipline__text">
                <span className="discipline__label">Domain 02</span>
                <h3 className="discipline__title">Modern Non-Firearm Weapons</h3>
                <p className="discipline__body">
                  Edged weapons, impact tools, and contemporary non-firearm platforms.
                  Training covers legal carry considerations, deployment under stress,
                  retention, and the ethical framework governing the use of force —
                  consistent with Axis principles at every tier.
                </p>
              </div>
            </div>

            <div className="discipline">
              <div className="discipline__image">
                <img src={FirearmsImg} alt="Firearms and CQB tactics" />
              </div>
              <div className="discipline__text">
                <span className="discipline__label">Domain 03</span>
                <h3 className="discipline__title">Firearms & CQB Tactics</h3>
                <p className="discipline__body">
                  Firearms proficiency and Close Quarters Battle tactics for real-world
                  application. Training covers marksmanship fundamentals, weapon handling
                  under stress, and CQB movement and clearance. All firearm training operates
                  within a strict ethical and legal framework.
                </p>
              </div>
            </div>

          </div>
        </div>

        {/* ── TACTICAL MEDICINE ── */}
        <div className="module" id="tactical__medical">
          <h2 className="module__heading">Tactical Medicine</h2>
          <p className="module__subheading">
            The capacity to end a threat means nothing if you cannot keep people alive
            afterward. Medical training is not optional — it is a core obligation of
            every Axis Tactical member.
          </p>

          <div className="disciplines__list">

            <div className="discipline">
              <div className="discipline__image">
                <img src={TCCCImg} alt="Tactical Combat Casualty Care" />
              </div>
              <div className="discipline__text">
                <span className="discipline__label">Domain 04</span>
                <h3 className="discipline__title">TCCC — Tactical Combat Casualty Care</h3>
                <p className="discipline__body">
                  Based on the same framework used by military and law enforcement, TCCC
                  training covers haemorrhage control, airway management, treatment of
                  penetrating trauma, and casualty extraction under duress. Every member
                  is trained to treat; Tier 1 members are trained to lead.
                </p>
              </div>
            </div>

            <div className="discipline discipline--reverse">
              <div className="discipline__image">
                <img src={FAImg} alt="Trauma first aid" />
              </div>
              <div className="discipline__text">
                <span className="discipline__label">Domain 04b</span>
                <h3 className="discipline__title">Trauma First Aid & Triage</h3>
                <p className="discipline__body">
                  Beyond combat scenarios — mass casualty triage, civilian trauma response,
                  and the decision-making frameworks required when resources are limited and
                  time is not. Grounded in the Axis principle of reverence for life: the
                  obligation to preserve it is as real as the obligation to defend it.
                </p>
              </div>
            </div>

          </div>
        </div>

        {/* ── CODE OF CONDUCT ── */}
        <div className="module" id="tactical__conduct">
          <div className="panel panel--conduct">
            <div className="panel__title">Code of Conduct</div>
            <p className="module__subheading">
              What separates Axis Tactical from any other armed group is not capability —
              it is the framework that governs when, why, and how that capability is used.
              These are not guidelines. They are conditions of membership.
            </p>
            <div className="panel__body">
              <div className="panel__row">
                <div className="panel__label">Force is a last resort</div>
                <div className="panel__value">
                  Axis Tactical members exhaust every available option before applying
                  physical force. The capacity for violence is maintained precisely so
                  it rarely needs to be used.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">The Sacred Three govern all action</div>
                <div className="panel__value">
                  No operation, mission, or individual action may be taken that
                  contradicts the principles of Life, The Planet, or the Social Contract.
                  If it conflicts with the Sacred Three, it does not happen.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">Mission over emotion</div>
                <div className="panel__value">
                  Operations are planned, purposeful, and proportionate. Personal grievance,
                  anger, or ego are not operational justifications. Members who cannot
                  maintain this standard are stood down.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">Accountability is non-negotiable</div>
                <div className="panel__value">
                  Every member is accountable to every other member, and to the
                  broader Axis organization. There is no rank that exempts anyone from
                  that accountability. Misconduct is addressed, not covered.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">The law is a floor, not a ceiling</div>
                <div className="panel__value">
                  Axis Tactical operates within the law. Where the law falls short of
                  what the Sacred Three demand, we hold ourselves to the higher standard —
                  not the lower one.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── FITNESS STANDARDS ── */}
        <div className="module" id="tactical__fitness">
          <h2 className="module__heading">Fitness Standards</h2>
          <p className="module__subheading">
            Physical capability is not a personal preference within Axis Tactical —
            it is a shared responsibility. Standards exist to ensure every member
            can be relied upon when it matters.
          </p>

          <div className="discipline tac-fitness__intro">
            <div className="discipline__image">
              <img src={FitnessImg} alt="Fitness training" />
            </div>
            <div className="discipline__text">
              <span className="discipline__label">Standards Overview</span>
              <h3 className="discipline__title">Progressive by Tier</h3>
              <p className="discipline__body">
                Fitness standards scale with tier and are tested at regular intervals.
                Progression to a higher tier requires meeting that tier's physical benchmarks
                before any other evaluation takes place. Standards cover endurance, strength,
                load-bearing capacity, and sustained output under stress — not peak athletic
                performance, but reliable operational capability.
              </p>
              <p className="discipline__body">
                Specific benchmarks are set by chapter leadership and reviewed annually.
                The figures below represent the current baseline framework.
              </p>
            </div>
          </div>

          <div className="tiers__grid tac-fitness__grid">

            <div className="tier tier--3">
              <div className="tier__badge">Tier 3 — Practitioner</div>
              <h3 className="tier__title">Baseline Standards</h3>
              <ul className="tier__list">
                <li>2-mile run — time to be set by chapter</li>
                <li>Push-ups — minimum reps, unbroken</li>
                <li>Pull-ups — minimum reps</li>
                <li>Basic load carry — distance and weight TBD</li>
                <li>Assessed quarterly</li>
              </ul>
            </div>

            <div className="tier tier--2">
              <div className="tier__badge">Tier 2 — Operator</div>
              <h3 className="tier__title">Operational Standards</h3>
              <ul className="tier__list">
                <li>Elevated run standard — faster or longer</li>
                <li>Weighted ruck march — distance and weight TBD</li>
                <li>Combat fitness assessment</li>
                <li>Sustained output under stress scenario</li>
                <li>Assessed bi-monthly</li>
              </ul>
            </div>

            <div className="tier tier--1">
              <div className="tier__badge">Tier 1 — Special Operator</div>
              <h3 className="tier__title">Elite Standards</h3>
              <ul className="tier__list">
                <li>Extended duration operations capacity</li>
                <li>Multi-day load-bearing endurance</li>
                <li>Recovery metrics under sustained stress</li>
                <li>Periodic re-certification — no grandfather clause</li>
                <li>Assessed monthly</li>
              </ul>
            </div>

          </div>
        </div>

        {/* ── MISSION LOG ── */}
        <div className="module" id="tactical__log">
          <div className="panel panel--log">
            <div className="panel__title">Mission Log</div>
            <p className="module__subheading">
              A record of completed operations, training events, and community actions.
              Axis Tactical is an active organization — this log exists to demonstrate
              that, and to hold the organization accountable to its own mission.
            </p>
            <div className="panel__body">

              <div className="panel__row">
                <div className="panel__label">Log Entry Placeholder</div>
                <div className="panel__value">
                  Date • Type: Community Action • Status: Complete<br />
                  Brief description of the event, its objective, and outcome. 
                  Add real entries here as operations and training events occur.
                </div>
              </div>

              <div className="panel__row">
                <div className="panel__label">Log Entry Placeholder</div>
                <div className="panel__value">
                  Date • Type: Training Exercise • Status: Complete<br />
                  Brief description of the event, its objective, and outcome.
                  Participation count and tier breakdown can be noted here.
                </div>
              </div>

              <div className="panel__row">
                <div className="panel__label">Log Entry Placeholder</div>
                <div className="panel__value">
                  Date • Type: Charity Operation • Status: Upcoming<br />
                  Brief description of the planned event, objective, and
                  any coordination requirements.
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ── CHAPTER STRUCTURE ── */}
        <div className="module" id="tactical__chapters">
          <h2 className="module__heading">Chapter Structure</h2>
          <p className="module__subheading">
            Axis Tactical is built to scale without losing cohesion. The chapter
            model allows local units to operate with autonomy while remaining
            accountable to the broader Axis mission and standards.
          </p>
          <div className="tiers__grid">

            <div className="tier tier--3">
              <div className="tier__badge">Local</div>
              <h3 className="tier__title">Cell / Local Chapter</h3>
              <p className="tier__body">
                The base unit of Axis Tactical. A local chapter is a small, trusted
                group operating within a defined geographic area. It trains together,
                acts together, and is accountable to its members first and the regional
                structure second.
              </p>
              <ul className="tier__list">
                <li>Minimum founding membership TBD</li>
                <li>Requires a Tier 2 or above chapter lead</li>
                <li>Independent training and scheduling</li>
                <li>Reports activity to regional chapter</li>
              </ul>
            </div>

            <div className="tier tier--2">
              <div className="tier__badge">Regional</div>
              <h3 className="tier__title">Regional Chapter</h3>
              <p className="tier__body">
                Regional chapters coordinate multiple local cells, organize larger
                operations, and maintain standards consistency across their area.
                They are the bridge between local autonomy and organizational coherence.
              </p>
              <ul className="tier__list">
                <li>Requires a Tier 1 regional lead</li>
                <li>Oversees local chapter standards and accountability</li>
                <li>Coordinates multi-chapter operations</li>
                <li>Interfaces directly with Axis command</li>
              </ul>
            </div>

            <div className="tier tier--1">
              <div className="tier__badge">Command</div>
              <h3 className="tier__title">Axis Command</h3>
              <p className="tier__body">
                The organizational authority for Axis Tactical as a whole. Command sets
                standards, approves new chapters, oversees mission alignment with the
                Sacred Three, and is the final accountability layer for the entire
                Tactical structure.
              </p>
              <ul className="tier__list">
                <li>Sets and updates operational standards</li>
                <li>Approves chapter formation and dissolution</li>
                <li>Mission authorization for Tier 1 operations</li>
                <li>Accountable to the broader Axis organization</li>
              </ul>
            </div>

          </div>
        </div>

      </section>
    </main>
  );
}
